import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, BookMarked, TrendingUp, TrendingDown, Minus, Calendar, School2, Loader2, X } from "lucide-react";
import { api, BRL } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Dialog para pesquisar o Catálogo de Serviços salvos e importar
 * descrição + valor para o item do orçamento sendo editado.
 *
 * Props:
 *  - open, onOpenChange
 *  - initialQuery (string, opcional) — prefill da busca ao abrir
 *  - onPick(entry) — callback ao clicar em "Usar"
 */
export default function ServiceCatalogDialog({ open, onOpenChange, initialQuery = "", onPick }) {
  const [q, setQ] = useState(initialQuery || "");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ count: 0, min: 0, max: 0, avg: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setQ(initialQuery || "");
  }, [open, initialQuery]);

  const fetchData = async (query) => {
    setLoading(true);
    try {
      const params = {};
      if (query && query.trim()) params.q = query.trim();
      const [list, stat] = await Promise.all([
        api.get("/services-catalog", { params: { ...params, size: 40, sort: "recent" } }),
        api.get("/services-catalog/stats", { params }),
      ]);
      setItems(list.data?.items || []);
      setStats(stat.data || { count: 0, min: 0, max: 0, avg: 0 });
    } catch (e) {
      toast.error("Falha ao carregar catálogo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchData(q), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);

  const spread = useMemo(() => (stats.max || 0) - (stats.min || 0), [stats]);

  const handlePick = async (entry) => {
    try {
      api.post(`/services-catalog/${entry.id}/use`).catch(() => {});
    } catch (e) { /* non-blocking */ }
    onPick?.(entry);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="service-catalog-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookMarked className="w-5 h-5 text-[#7c3aed]" />
            Catálogo de serviços
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Base histórica de valores já cobrados. Clique em "Usar" para preencher o item.
          </p>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por descrição... (ex: lâmpada, pintura, telha)"
            className="pl-9 pr-9 h-10"
            data-testid="input-catalog-search"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label="Limpar"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Insights */}
        {stats.count > 0 && (
          <div className="grid grid-cols-4 gap-2 bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl p-3 border border-violet-100" data-testid="catalog-stats">
            <div className="text-center">
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Registros</p>
              <p className="text-base font-black text-slate-900 tabular-nums">{stats.count}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide flex items-center justify-center gap-0.5"><TrendingDown className="w-3 h-3" /> Mín</p>
              <p className="text-sm font-black text-emerald-700 tabular-nums">{BRL(stats.min)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-violet-600 font-semibold uppercase tracking-wide flex items-center justify-center gap-0.5"><Minus className="w-3 h-3" /> Média</p>
              <p className="text-sm font-black text-violet-700 tabular-nums">{BRL(stats.avg)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-rose-600 font-semibold uppercase tracking-wide flex items-center justify-center gap-0.5"><TrendingUp className="w-3 h-3" /> Máx</p>
              <p className="text-sm font-black text-rose-700 tabular-nums">{BRL(stats.max)}</p>
            </div>
          </div>
        )}

        {/* List */}
        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carregando...
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">
              {q ? "Nenhum registro encontrado para esta busca." : "Catálogo vazio. Salve itens direto dos orçamentos."}
            </div>
          ) : (
            items.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => handlePick(it)}
                className="w-full text-left bg-white border border-slate-200 hover:border-[#7c3aed] hover:shadow-md rounded-xl p-3 transition-all group"
                data-testid={`catalog-item-${it.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 line-clamp-2">{it.description}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-500">
                      {it.school_name && (
                        <span className="inline-flex items-center gap-0.5"><School2 className="w-3 h-3" /> {it.school_name}</span>
                      )}
                      {it.budget_number && (
                        <span>Orç. #{it.budget_number}</span>
                      )}
                      {it.created_at && (
                        <span className="inline-flex items-center gap-0.5"><Calendar className="w-3 h-3" /> {new Date(it.created_at).toLocaleDateString("pt-BR")}</span>
                      )}
                      {it.times_used > 0 && (
                        <span className="text-violet-600 font-semibold">Reusado {it.times_used}x</span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    {it.min_value != null && it.max_value != null && it.min_value !== it.max_value ? (
                      <>
                        <p className="text-sm sm:text-base font-black text-[#7c3aed] tabular-nums whitespace-nowrap leading-tight">
                          {BRL(it.min_value)} <span className="text-slate-400 font-normal">—</span> {BRL(it.max_value)}
                        </p>
                        <p className="text-[10px] text-slate-500 tabular-nums">
                          méd. {BRL((Number(it.min_value) + Number(it.max_value)) / 2)}
                        </p>
                      </>
                    ) : (
                      <p className="text-lg font-black text-[#7c3aed] tabular-nums">{BRL(it.value)}</p>
                    )}
                    <span className="inline-block mt-1 text-[10px] font-bold text-slate-400 group-hover:text-[#7c3aed]">USAR →</span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
